import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Loader from "../../components/Loader";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Search, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";

const inputClass =
  "w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition";

const labelClass = "block text-sm font-medium text-slate-600 mb-1";

const Brands = () => {
  const [search, setSearch] = useState("");
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newBrand, setNewBrand] = useState({
    name: "",
    image: null,
  });

  // For delete confirmation modal
  const [deleteBrandObj, setDeleteBrandObj] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/brands");
        setBrands(res.data || []);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  /* ================= MODAL HANDLERS ================= */
  const openModal = () => {
    setEditingId(null);
    setNewBrand({ name: "", image: null });
    setImagePreview("");
    setShowModal(true);
  };

  const openEdit = (brand) => {
    setEditingId(brand._id);
    setNewBrand({ name: brand.name, image: null });
    setImagePreview(
      brand.image ? `${process.env.REACT_APP_IMAGE_URL}/${brand.image}` : ""
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setNewBrand({ name: "", image: null });
    setImagePreview("");
  };

  /* ================= IMAGE ================= */
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewBrand((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  /* ================= SAVE ================= */
  const saveBrand = async () => {
    if (!newBrand.name.trim()) return alert("Brand name required");

    const formData = new FormData();
    formData.append("name", newBrand.name);

    if (newBrand.image instanceof File) {
      formData.append("uploadImage", newBrand.image);
    }

    try {
      setIsSaving(true); // ⭐ START LOADER

      let res;

      if (editingId) {
        res = await apiClient.put(`/brand/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setBrands((prev) =>
          prev.map((b) => (b._id === editingId ? res.data.brand : b))
        );
      } else {
        res = await apiClient.post("/add-brand", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setBrands((prev) => [res.data.brand, ...prev]);
      }

      closeModal();
    } catch (err) {
      alert("Error saving brand");
    } finally {
      setIsSaving(false); // ⭐ STOP LOADER
    }
  };

  /* ================= DELETE ================= */
  const handleDeleteBrand = async () => {
    if (!deleteBrandObj) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/brand/${deleteBrandObj._id}`);
      setBrands((prev) => prev.filter((b) => b._id !== deleteBrandObj._id));
      setDeleteBrandObj(null);
    } catch (err) {
      alert("Failed to delete brand");
    } finally {
      setDeleting(false);
    }
  };

  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>

      {/* HEADER - cloned from settings page */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Brand Management</h1>
          <p className="text-sm text-slate-500 mt-1">Add, edit, or remove product brands.</p>
        </div>
        <button
          onClick={openModal}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          Add Brand
        </button>
      </div>

      {/* SEARCH - cloned from Add User input design */}
        {/* SEARCH - icon inside input */}
        <div className="relative mb-6 max-w-md mx-auto">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </span>
          <input
            placeholder="Search brand..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
          />
        </div>

      {/* GRID */}
      <div className="min-h-[120px] border-t">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Loader size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {filteredBrands.map((brand) => (
              <div
                key={brand._id}
                className="bg-white p-2 border-b border-e "
              >
                <div>
                  <div className="w-full aspect-square bg-gray-100 rounded-md overflow-hidden border max-h-40">
                    <img
                      src={
                        brand.image
                          ? `${process.env.REACT_APP_IMAGE_URL}/${brand.image}`
                          : "https://via.placeholder.com/300"
                      }
                      alt={brand.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-sm font-medium text-center mt-2 truncate">
                    {brand.name}
                  </h2>
                  <div className="mt-2 flex justify-center gap-2">
                    <button
                      onClick={() => openEdit(brand)}
                      className="p-1.5 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteBrandObj(brand)}
                      className="p-1.5 rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition-colors"
                      title="Delete brand"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= MODAL (SAME THEME) ================= */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveBrand();
            }}
            className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* HEADER */}
            <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
              <h2 className="text-lg font-semibold">

                {editingId ? "Edit Brand" : "Add Brand"}
              </h2>
              <button type="button" onClick={closeModal} className="text-xl text-white">
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              {/* IMAGE */}
              <div className="flex flex-col items-center">
                <div className="relative w-32 h-32 border rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Brand Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">No Image</span>
                  )}
                </div>


                <label className="mt-3 cursor-pointer bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 py-2 rounded-md flex items-center gap-2 text-sm">
                  <Upload className="w-4 h-4" /> Upload Image
                  <input type="file" hidden accept="image/*" onChange={handleImage} />
                </label>
              </div>

              {/* NAME */}
              <div>
                <label className="text-xs font-medium text-slate-700">Brand Name</label>
                <input
                  type="text"
                  placeholder="Enter brand name"
                  value={newBrand.name}
                  onChange={e => setNewBrand(prev => ({ ...prev, name: e.target.value }))}
                  className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
              >
                {isSaving ? (
                  <Loader text={editingId ? "Updating..." : "Saving..."} inline />
                ) : (
                  editingId ? "Update Brand" : "Save Brand"
                )}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    {/* Delete Brand Confirmation Modal */}
    <ConfirmDialog
      open={!!deleteBrandObj}
      title="Delete Brand"
      description={`Are you sure you want to delete ${deleteBrandObj?.name}? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
      loading={deleting}
      onCancel={() => setDeleteBrandObj(null)}
      onConfirm={handleDeleteBrand}
    />
  </div>
  );
};

export default Brands;
